import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";

const FLAG_META = {
  MINOR_UNDER_12:            { level: "high",   text: "Menor de 12 años — revisar prescripción" },
  MINOR_UNDER_16:            { level: "medium", text: "Menor de 16 años — supervisión especial" },
  VERY_YOUNG_REVIEW:         { level: "high",   text: "Muy joven — revisar idoneidad del programa" },
  REPORTS_CURRENT_PAIN:      { level: "high",   text: "Reporta dolor actual" },
  SIGNIFICANT_INJURY_HISTORY:{ level: "high",   text: "Historial de lesiones significativas" },
  LOW_SLEEP:                 { level: "medium", text: "Sueño insuficiente (<7h)" },
  HIGH_SCHOOL_LOAD:          { level: "medium", text: "Carga escolar muy alta" },
  NO_GYM_EXPERIENCE:         { level: "medium", text: "Sin experiencia previa en gimnasio" },
};

export default function AdminDiagnosticDetail() {
  const { id } = useParams();
  const [sub, setSub] = useState(null);
  const [schema, setSchema] = useState(null);
  const [coachNotes, setCoachNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: sc }] = await Promise.all([
          api.get(`/admin/diagnostics/submissions/${id}`),
          api.get("/admin/diagnostics/schema"),
        ]);
        setSub(s);
        setCoachNotes(s.coach_notes || "");
        setSchema(sc.schema);
      } catch (e) {
        toast.error(formatErr(e));
      }
    })();
  }, [id]);

  const markReviewed = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/diagnostics/submissions/${id}/mark-reviewed`, { coach_notes: coachNotes });
      toast.success("Marcado como revisado");
      setSub({ ...sub, status: "REVIEWED", coach_notes: coachNotes });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (!sub) return <AppShell><div style={{ padding: 40, color: "#a3a3a3" }}>Cargando…</div></AppShell>;

  const fieldLabel = (id) => {
    for (const s of schema?.sections || []) {
      for (const f of s.fields || []) if (f.id === id) return f.label;
    }
    return id;
  };

  return (
    <AppShell>
      <Link to="/app/admin/diagnosticos" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#a3a3a3", textDecoration: "none", fontSize: 13, marginBottom: 16 }}>
        <ArrowLeft size={14} /> Volver a diagnósticos
      </Link>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2rem", letterSpacing: "0.06em", margin: 0 }}>
            {sub.player_name_hint || "Diagnóstico"}
          </h1>
          <div style={{ fontSize: 12, color: "#a3a3a3", marginTop: 4 }}>
            Enviado {new Date(sub.submitted_at).toLocaleString()} · Estado: <b style={{ color: sub.status === "REVIEWED" ? "#4ade80" : "#facc15" }}>{sub.status}</b>
          </div>
        </div>
        {sub.status !== "REVIEWED" && (
          <button onClick={markReviewed} disabled={saving} data-testid="diag-mark-reviewed" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#16a34a", color: "#fff", border: "none", padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", borderRadius: 2 }}>
            <CheckCircle2 size={16} /> {saving ? "Guardando…" : "Marcar como revisado"}
          </button>
        )}
      </div>

      {sub.safety_flags && sub.safety_flags.length > 0 && (
        <div style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.3)", padding: 16, borderRadius: 4, marginBottom: 20 }} data-testid="diag-safety-flags">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, color: "#f87171", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600 }}>
            <AlertTriangle size={16} /> Flags de atención
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {sub.safety_flags.map((f) => {
              const m = FLAG_META[f] || { level: "medium", text: f };
              const c = m.level === "high" ? "#f87171" : "#facc15";
              return <span key={f} style={{ padding: "5px 10px", background: `${c}20`, color: c, fontSize: 12, borderRadius: 999, border: `1px solid ${c}40` }}>{m.text}</span>;
            })}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(280px, 1fr)", gap: 20 }} className="qv-diag-detail-grid">
        <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4, padding: 20, minWidth: 0 }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 13, letterSpacing: "0.16em", textTransform: "uppercase", color: "#a3a3a3" }}>Respuestas del padre</h3>
          {schema?.sections?.map((sec) => (
            <div key={sec.id} style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: "Bebas Neue, sans-serif", letterSpacing: "0.14em", fontSize: 18, color: "#dc2626", marginBottom: 10 }}>{sec.title}</div>
              <div style={{ display: "grid", gap: 8 }}>
                {sec.fields.map((f) => {
                  const v = sub.answers[f.id];
                  if (v === undefined || v === "" || v === null) return null;
                  return (
                    <div key={f.id} className="qv-diag-answer-row" style={{ display: "grid", gridTemplateColumns: "minmax(140px, 180px) 1fr", gap: 12, padding: "8px 0", borderBottom: "1px dashed rgba(255,255,255,0.05)" }}>
                      <div style={{ fontSize: 12, color: "#a3a3a3" }}>{f.label}</div>
                      <div style={{ fontSize: 14, color: "#fff", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{String(v === true ? "Sí" : v === false ? "No" : v)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4, padding: 20 }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 13, letterSpacing: "0.16em", textTransform: "uppercase", color: "#a3a3a3", display: "flex", alignItems: "center", gap: 6 }}>
              <ShieldCheck size={14} /> Notas del coach
            </h3>
            <textarea
              rows={10}
              value={coachNotes}
              onChange={(e) => setCoachNotes(e.target.value)}
              placeholder="Observaciones, plan de acción propuesto, próximos pasos…"
              data-testid="diag-coach-notes"
              style={{ width: "100%", background: "#111", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "10px 12px", fontSize: 14, borderRadius: 3, outline: "none", boxSizing: "border-box", resize: "vertical", minHeight: 200 }}
            />
            <p style={{ fontSize: 11, color: "#737373", marginTop: 8 }}>
              Este diagnóstico requiere revisión manual. Nunca se creará un plan de forma automática.
            </p>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
